import { FlexRowAlignCenter } from "@/components/Flex";
import { useNotePageContextSelector } from "../../hooks/NotePageContext";
import { useMemo } from "react";
import { UNTITLED } from "@knowt/syncing/utils/dataCleaning";
import SidebarSelect from "./styled/SidebarSelect";

const FoldersFilter = () => {
    const note = useNotePageContextSelector(state => state.note);
    const explorerFilters = useNotePageContextSelector(state => state.explorerFilters);
    const explorerFolders = useNotePageContextSelector(state => state.explorerFolders);

    const setExplorerFilters = useNotePageContextSelector(state => state.setExplorerFilters);

    const folders = useMemo(() => {
        return Object.values(explorerFolders || {}).map(folder => ({
            label: folder.name ?? UNTITLED,
            value: folder.folderId,
        }));
    }, [explorerFolders]);

    if (note.classId) return null;

    return (
        <SidebarSelect
            options={[{ label: "All Folders", value: null }, ...folders]}
            selected={
                folders.find(folder => folder.value === explorerFilters.folderId) ?? {
                    label: "All Folders",
                    value: null,
                }
            }
            onChange={({ value }) => setExplorerFilters({ ...explorerFilters, folderId: value })}
        />
    );
};

const TagsFilter = () => {
    const notes = useNotePageContextSelector(state => state.explorerNotes);
    const explorerFilters = useNotePageContextSelector(state => state.explorerFilters);
    const setExplorerFilters = useNotePageContextSelector(state => state.setExplorerFilters);

    const tags = useMemo(() => {
        const uniqueTags = new Set<string>();
        return Object.values(notes || {}).flatMap(note => {
            const tags: { label: string; value: string }[] = [];
            if (note.exam_v2 && !uniqueTags.has(note.exam_v2)) {
                uniqueTags.add(note.exam_v2);
                tags.push({
                    label: note.exam_v2,
                    value: note.exam_v2,
                });
            }
            if (note.examSection && !uniqueTags.has(note.examSection)) {
                uniqueTags.add(note.examSection);
                tags.push({
                    label: note.examSection,
                    value: note.examSection,
                });
            }
            return tags;
        });
    }, [notes]);

    return (
        <SidebarSelect
            options={[{ label: "All Tags", value: null }, ...tags]}
            selected={tags.find(tag => tag.value === explorerFilters.tag) ?? { label: "Tags", value: null }}
            onChange={({ value }) => setExplorerFilters({ ...explorerFilters, tag: value })}
        />
    );
};

const ClassesFilter = () => {
    const note = useNotePageContextSelector(state => state.note);
    const notes = useNotePageContextSelector(state => state.explorerNotes);
    const userClasses = useNotePageContextSelector(state => state.userClasses);

    const explorerFilters = useNotePageContextSelector(state => state.explorerFilters);
    const setExplorerFilters = useNotePageContextSelector(state => state.setExplorerFilters);

    const isNoteOwner = useNotePageContextSelector(state => state.isNoteOwner);

    const classes = useMemo(() => {
        const uniqueClasses = new Set();
        return Object.values(notes || {})
            .map(note => ({
                label: (userClasses && note.classId && userClasses[note.classId]?.name) || "Unknown",
                value: note.classId || null,
            }))
            .filter(course => {
                if (!course.value || uniqueClasses.has(course.value)) {
                    return false;
                }
                uniqueClasses.add(course.value);
                return true;
            });
    }, [notes, userClasses]);

    if (!isNoteOwner || !note.classId) return null;

    return (
        <SidebarSelect
            options={[{ label: "All Classes", value: null }, ...classes]}
            selected={
                classes.find(course => course.value === explorerFilters.classId) ?? { label: "Class", value: null }
            }
            onChange={({ value }) => setExplorerFilters({ ...explorerFilters, classId: value })}
        />
    );
};

const FiltersBar = ({ style }: { style?: React.CSSProperties }) => {
    return (
        <FlexRowAlignCenter style={{ gap: "1rem", ...style }}>
            <FoldersFilter />
            <ClassesFilter />
            <TagsFilter />
        </FlexRowAlignCenter>
    );
};

export default FiltersBar;
